import React, { useEffect, useState } from 'react';
import { Loader2, ScrollText, ChevronDown, ChevronRight } from 'lucide-react';
import { atlasApi, AtlasUser, AuditLog } from '../shared/atlasApi';
import AvatarColaborador from '../shared/AvatarColaborador';

interface Props {
  user: AtlasUser;
}

const ACOES: { id: string; label: string }[] = [
  { id: '', label: 'Todas as ações' },
  { id: 'colaborador.editar', label: 'Editar ficha' },
  { id: 'colaborador.foto', label: 'Atualizar foto' },
  { id: 'permissao.alterar', label: 'Alterar permissão' },
  { id: 'disc.responder', label: 'Responder DISC' },
];

function chipAcao(acao: string): { label: string; classes: string } {
  switch (acao) {
    case 'colaborador.editar': return { label: 'Editar ficha', classes: 'bg-blue-100 text-blue-700' };
    case 'colaborador.foto': return { label: 'Foto', classes: 'bg-cyan-100 text-cyan-700' };
    case 'permissao.alterar': return { label: 'Permissão', classes: 'bg-orange-100 text-orange-700' };
    case 'disc.responder': return { label: 'DISC', classes: 'bg-purple-100 text-purple-700' };
    default: return { label: acao, classes: 'bg-gray-100 text-gray-600' };
  }
}

function formatarTs(s?: string): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; }
}

const PER_PAGE = 50;

const AuditoriaPage: React.FC<Props> = ({ user }) => {
  const [itens, setItens] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [expandido, setExpandido] = useState<string | null>(null);

  const [ator, setAtor] = useState('');
  const [atorDebounce, setAtorDebounce] = useState('');
  const [acao, setAcao] = useState('');
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setAtorDebounce(ator), 300);
    return () => clearTimeout(t);
  }, [ator]);

  useEffect(() => { setPage(1); }, [atorDebounce, acao, desde, ate]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    atlasApi.listarAuditoria(user, {
      ator: atorDebounce || undefined,
      acao: acao || undefined,
      desde: desde ? new Date(desde).toISOString() : undefined,
      ate: ate ? new Date(ate).toISOString() : undefined,
      page,
      per_page: PER_PAGE,
    })
      .then(r => { if (!cancel) { setItens(r.itens || []); setTotal(r.total || 0); } })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar auditoria'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, atorDebounce, acao, desde, ate, page]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const inputCls = 'px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400';

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
          <ScrollText className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900">Auditoria</h2>
          <p className="text-sm text-gray-500">Trilha de operações sensíveis (LGPD).</p>
        </div>
        <span className="text-xs text-gray-400">{total} registro{total === 1 ? '' : 's'}</span>
      </div>

      {/* Filtros */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block">Ator (email)</label>
          <input value={ator} onChange={e => setAtor(e.target.value)} placeholder="email@empresa.com" className={`${inputCls} w-full`} />
        </div>
        <div>
          <label className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block">Ação</label>
          <select value={acao} onChange={e => setAcao(e.target.value)} className={inputCls}>
            {ACOES.map(a => <option key={a.id || 'todas'} value={a.id}>{a.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block">Desde</label>
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block">Até</label>
          <input type="date" value={ate} onChange={e => setAte(e.target.value)} className={inputCls} />
        </div>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading && itens.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...
          </div>
        ) : itens.length === 0 ? (
          <div className="p-12 text-center text-gray-400">Nenhum registro encontrado.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Quando</th>
                <th className="px-4 py-2 text-left">Ator</th>
                <th className="px-4 py-2 text-left">Ação</th>
                <th className="px-4 py-2 text-left">Alvo</th>
                <th className="px-4 py-2 text-right">Detalhes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {itens.map(it => {
                const chip = chipAcao(it.acao);
                const aberto = expandido === it._id;
                return (
                  <React.Fragment key={it._id}>
                    <tr className="hover:bg-gray-50/60">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatarTs(it.timestamp)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <AvatarColaborador nome={it.ator_email} size={28} />
                          <span className="text-gray-700 truncate max-w-[200px]">{it.ator_email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${chip.classes}`}>{chip.label}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <span className="text-gray-400">{it.alvo_tipo}</span> #{it.alvo_id}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setExpandido(aberto ? null : it._id)}
                          className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
                        >
                          {aberto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {aberto ? 'Ocultar' : 'Ver'}
                        </button>
                      </td>
                    </tr>
                    {aberto && (
                      <tr className="bg-gray-50/60">
                        <td colSpan={5} className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {it.antes !== undefined && it.antes !== null && (
                              <div>
                                <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Antes</div>
                                <pre className="text-xs bg-white border border-gray-200 rounded-lg p-2 overflow-x-auto text-gray-700">{JSON.stringify(it.antes, null, 2)}</pre>
                              </div>
                            )}
                            {it.depois !== undefined && it.depois !== null && (
                              <div>
                                <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Depois</div>
                                <pre className="text-xs bg-white border border-gray-200 rounded-lg p-2 overflow-x-auto text-gray-700">{JSON.stringify(it.depois, null, 2)}</pre>
                              </div>
                            )}
                            {it.contexto && Object.keys(it.contexto).length > 0 && (
                              <div>
                                <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Contexto</div>
                                <pre className="text-xs bg-white border border-gray-200 rounded-lg p-2 overflow-x-auto text-gray-700">{JSON.stringify(it.contexto, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            ← Anterior
          </button>
          <span className="text-gray-500">Página {page} de {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Próxima →
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditoriaPage;
